// crewbit-admin/src/pages/Money/Disputes/index.tsx
//
// Tier 2-UI Phase E (in-app bundle) — admin disputes page. Operator-facing
// canonical surface (per §0.11 admin is PRIMARY for review-and-respond).
// Composes PageHeader + counts strip + list + slide-over via URL state
// (?dispute_id=). Mirrors Outstanding/Expenses page pattern from Sessions
// 123/127/129/136.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { EmptyState } from '../../../components/common/EmptyState';
import { Skeleton } from '../../../components/common/Skeleton';
import { Toast } from '../../../components/common/Toast';
import { MoneyDisputeDetailPanel } from '../../../components/money/MoneyDisputeDetailPanel';
import DisputeListRow from '../../../components/money/DisputeListRow';
import SettingsGearLink from '../../../components/money/settings/SettingsGearLink';
import { useActiveBusiness } from '../../../hooks/useActiveBusiness';
import { useDisputeEscalations } from '../../../hooks/useDisputeEscalations';
import { admin, palette, radii, spacing, typography } from '../../../styles/tokens';
import type { DisputeEscalation, DisputeStatus } from '../../../types/disputes';

const STATUS_LABELS: Record<DisputeStatus, string> = {
  opened: 'Open',
  evidence_submitted: 'Under review',
  won: 'Won',
  lost: 'Lost',
};

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

export function MoneyDisputesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const business = useActiveBusiness();
  const businessId = business.activeBusinessId ?? null;
  const fetchDisputes = useDisputeEscalations();
  const disputeIdParam = searchParams.get('dispute_id');
  const invoiceIdParam = searchParams.get('invoice_id');

  const [disputes, setDisputes] = useState<DisputeEscalation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    try {
      const result = await fetchDisputes({ businessId });
      if (result.success) {
        setDisputes(result.data.disputes);
        setErrorCode(null);
      } else {
        setErrorCode(result.error.code);
        setToast({ msg: `Couldn't load disputes (${result.error.code})`, type: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [businessId, fetchDisputes]);

  useEffect(() => {
    document.title = 'CrewBit · Disputes';
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-route ?invoice_id=... → ?dispute_id=... when filter yields exactly 1
  // match. Keeps DisputeBanner deep-link UX symmetric with mobile.
  useEffect(() => {
    if (!invoiceIdParam) return;
    if (disputeIdParam) return;
    const matches = disputes.filter((d) => d.invoice_id === invoiceIdParam);
    if (matches.length === 1) {
      setSearchParams({ dispute_id: matches[0].id });
    }
  }, [invoiceIdParam, disputeIdParam, disputes, setSearchParams]);

  const filtered = invoiceIdParam
    ? disputes.filter((d) => d.invoice_id === invoiceIdParam)
    : disputes;

  const handleTap = (dispute: DisputeEscalation) => {
    setSearchParams({ dispute_id: dispute.id });
  };

  const detailPanel = (
    <MoneyDisputeDetailPanel
      disputeId={disputeIdParam}
      onClose={() => setSearchParams({})}
    />
  );

  if (business.requiresSelection) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Disputes" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <EmptyState
          title="Pick a business"
          subtitle="Switch to an active business from the sidebar to see disputes."
        />
        {detailPanel}
      </div>
    );
  }

  if (isLoading && disputes.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader
          title="Disputes" back={{ to: '/money', label: 'Money' }}
          secondaryActions={<SettingsGearLink />}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={88} />
          ))}
        </div>
        {detailPanel}
      </div>
    );
  }

  if (errorCode && disputes.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Disputes" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <EmptyState
          title="Couldn't load disputes"
          subtitle={errorCode}
          actionLabel="Retry"
          onAction={() => void load()}
        />
        {detailPanel}
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader
          title="Disputes" back={{ to: '/money', label: 'Money' }}
          secondaryActions={
            <>
              <button
                type="button"
                onClick={() => void load()}
                style={refreshBtn}
                aria-label="Refresh"
              >
                <RefreshCw size={14} color={palette.g600} />
              </button>
              <SettingsGearLink />
            </>
          }
        />
        <div style={emptyCardStyle}>
          <CheckCircle2 size={32} color={palette.green} strokeWidth={2} />
          <div style={emptyTitleStyle}>No disputes</div>
          <div style={emptyBodyStyle}>Your invoices are settling cleanly.</div>
        </div>
        {detailPanel}
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Disputes" back={{ to: '/money', label: 'Money' }}
        secondaryActions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              style={refreshBtn}
              aria-label="Refresh"
            >
              <RefreshCw size={14} color={palette.g600} />
            </button>
            <SettingsGearLink />
          </>
        }
      />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {filtered.map((dispute) => (
          <DisputeListRow
            key={dispute.id}
            dispute={dispute}
            onTap={handleTap}
            statusLabels={STATUS_LABELS}
            invoiceMissingLabel="Invoice unavailable"
            clientUnknownLabel="Unknown client"
          />
        ))}
      </div>
      {detailPanel}
      <Toast
        message={toast?.msg ?? ''}
        type={toast?.type ?? 'info'}
        isVisible={!!toast}
        onDismiss={() => setToast(null)}
      />
    </div>
  );
}

const refreshBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing.xs,
  padding: '6px 10px',
  borderRadius: radii.sm,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: typography.size.caption,
  color: palette.g700,
};

const emptyCardStyle: React.CSSProperties = {
  background: palette.white,
  borderRadius: radii.lg,
  border: `1px solid ${palette.g150}`,
  padding: spacing.xl,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.sm,
};

const emptyTitleStyle: React.CSSProperties = {
  fontSize: typography.size.subheading,
  fontWeight: 600,
  color: palette.g800,
  marginTop: spacing.sm,
};

const emptyBodyStyle: React.CSSProperties = {
  fontSize: typography.size.body,
  color: palette.g600,
};
